import { createContext, useContext, useEffect } from "react";
import { useWeekViewContext } from "../WeekView/WeekViewContext";
import type { RegisteredSidePanelHost, SidePanelHost } from "./sidePanelTypes";

/**
 * The registered staffing page, split in two so registering never re-renders the readers of the drawer state: pages
 * only reach the (stable) setter, and only the panel reads the value. `RecommendationsDrawerProvider` provides both.
 */
export const SidePanelHostValueContext = createContext<RegisteredSidePanelHost | null>(null);
export const SidePanelHostSetterContext = createContext<((host: RegisteredSidePanelHost | null) => void) | null>(null);

/**
 * Registers the calling page as the side panel's host for as long as it is mounted: the panel docks beside it and
 * shows its page-bound tabs. Pass a memoized `host` — every new object re-renders the panel. Every host is a staffing
 * page, inside the staffing routes' `WeekViewProvider`, so its week view is registered with it.
 */
export const useSidePanelHost = (host: SidePanelHost): void => {
	const setHost = useContext(SidePanelHostSetterContext);
	const weekView = useWeekViewContext();
	if (setHost === null) {
		throw Error("`useSidePanelHost` must be used within a `RecommendationsDrawerProvider`");
	}

	useEffect(() => {
		setHost({ ...host, weekView });
	}, [host, weekView, setHost]);

	// Cleared only on unmount, so an update never passes through "no host" (which would undock the panel for a frame).
	useEffect(() => () => setHost(null), [setHost]);
};

/** The page the panel sits on, or `null` where no staffing page is mounted (the panel then overlays the page). */
export const useSidePanelHostValue = (): RegisteredSidePanelHost | null => useContext(SidePanelHostValueContext);
